import React, { useContext, useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useTransportService from '../../../app/services/hooks/useTransportService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { TransportDataForm, defaultTransportDataForm } from '../../../app/models/Transport';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import TransportForm from '../components/forms/TransportForm';
import { getTodayLocalDateString } from '../../../helpers';

type RouterParams = {
    id: string;
};

const TransportEdit = () => {
    const { id } = useParams<RouterParams>();
    const location = useLocation();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    // Pestaña activa desde la URL (?tab=nodos | ?tab=entregas). Por defecto: nodos.
    const tabParam = new URLSearchParams(location.search).get('tab');
    const activeTabKey = tabParam === 'entregas' ? 'deliveries' : 'nodes';
    const handleTabChange = (key: string | null) => {
        if (key) {
            const tabValue = key === 'deliveries' ? 'entregas' : 'nodos';
            history.replace({ pathname: location.pathname, search: `?tab=${tabValue}` });
        }
    };

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Transportes',
            url: '/transports',
            isActive: false
        },
        {
            name: 'Editar #' + id,
            url: '/transports/' + id + '/edit',
            isActive: true
        }
    ];

    const { editTransport, updateTransport, fetchingEditTransport, fetchingUpdateTransport } = 
        useTransportService();

    const [transport, setTransport] = useState<TransportDataForm>(defaultTransportDataForm);
    const [statusChoices, setStatusChoices] = useState<{ id: string | number; name: string }[]>([]);
    const [errorFields, setErrorFields] = useState<any>();

    const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<{ value: string; label: string } | null>(null);
    const [startDateForInTransit, setStartDateForInTransit] = useState('');
    const [endDateForFinalize, setEndDateForFinalize] = useState('');

    useEffect(() => {
        edit(parseInt(id));
    }, [id]);

    const edit = (id: number) => {
        editTransport(id, {
            onSuccess: (response: ServiceResponse) => {
                const transportData = response.data.transport;
                // Convertir los nodos del formato del backend al formato del formulario
                const nodes = transportData.nodes?.map((node: any) => ({
                    id: node.id,
                    location_id: node.location_id,
                    location_name: node.location_name,
                    sequence: node.sequence
                })) || [];
                
                // Convertir las entregas del formato del backend al formato del formulario
                const deliveryIds = transportData.deliveries?.map((delivery: any) => delivery.id).filter((id: any) => id !== null && id !== undefined) || [];
                
                // Formatear la fecha para el input type="date" (YYYY-MM-DD)
                let formattedDate = null;
                if (transportData.dispatch_date) {
                    // Si viene en formato YYYY-MM-DD, usarlo directamente
                    // Si viene en otro formato, parsearlo
                    formattedDate = transportData.dispatch_date.includes('-') 
                        ? transportData.dispatch_date.split('T')[0] // Tomar solo la parte de fecha si viene con hora
                        : transportData.dispatch_date;
                }
                
                setTransport({
                    id: transportData.id,
                    vehicle_id: transportData.vehicle_id,
                    dispatch_date: formattedDate,
                    status: transportData.status || 'CREATED',
                    status_label: transportData.status_label,
                    start_date: transportData.start_date?.slice?.(0, 10) ?? transportData.start_date ?? null,
                    end_date: transportData.end_date?.slice?.(0, 10) ?? transportData.end_date ?? null,
                    nodes: nodes,
                    delivery_ids: deliveryIds
                });
                setStatusChoices(response.data.status_choices || []);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data?.to_dashboard) {
                    navigationPage('/transports');
                }
            }
        });
    };

    const update = (overridePayload?: Partial<TransportDataForm>) => {
        const payload = transport;
        const dataToSend: TransportDataForm = {
            vehicle_id: overridePayload?.vehicle_id ?? payload.vehicle_id,
            dispatch_date: overridePayload?.dispatch_date ?? payload.dispatch_date ?? null,
            status: overridePayload?.status ?? payload.status,
            start_date: overridePayload?.start_date ?? payload.start_date ?? null,
            end_date: overridePayload?.end_date ?? payload.end_date ?? null,
            nodes: payload.nodes?.map(node => ({
                id: node.id,
                location_id: node.location_id || 0,
                sequence: node.sequence || 0
            })).filter(node => node.location_id && node.location_id > 0) || [],
            delivery_ids: payload.delivery_ids || []
        };

        if (showLoading) showLoading('loading', 'Actualizando transporte...');

        updateTransport(Number(transport?.id), dataToSend, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                edit(parseInt(id));
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const handleStatusChange = (newStatus: string, label: string) => {
        const today = getTodayLocalDateString();
        setPendingStatus({ value: newStatus, label });
        if (newStatus === 'IN_TRANSIT') {
            setStartDateForInTransit((transport.start_date || '').slice(0, 10) || today);
            setEndDateForFinalize('');
        } else {
            setStartDateForInTransit('');
            setEndDateForFinalize((transport.end_date || '').slice(0, 10) || today);
        }
        setShowStatusConfirmModal(true);
    };

    const confirmStatusChange = () => {
        if (!pendingStatus) return;
        const updated: Partial<TransportDataForm> = {
            ...transport,
            status: pendingStatus.value,
            start_date: pendingStatus.value === 'IN_TRANSIT' && startDateForInTransit
                ? startDateForInTransit.slice(0, 10)
                : transport.start_date,
            end_date: pendingStatus.value === 'FINALIZED' && endDateForFinalize
                ? endDateForFinalize.slice(0, 10)
                : transport.end_date
        };
        setTransport(prev => ({ ...prev, ...updated }));
        update(updated);
        setShowStatusConfirmModal(false);
        setPendingStatus(null);
        setStartDateForInTransit('');
        setEndDateForFinalize('');
    };

    return (
        <>
            <Breadcrumbs pageSection={`Editar Transporte #${id}`} breadcrumbs={breadcrumbs} />
            
            <DefaultCard>
                <div className="h3 mt-0 mb-2 card-title">Datos Generales</div>
                
                {!fetchingEditTransport ? (
                    <>
                        <TransportForm
                            transport={transport}
                            setTransport={setTransport}
                            errorFields={errorFields}
                            is_edit={true}
                            statusChoices={statusChoices}
                            onStatusChange={handleStatusChange}
                            activeTabKey={activeTabKey}
                            onTabChange={handleTabChange}
                            onNavigateToDelivery={(deliveryId) => navigationPage('/deliveries/' + deliveryId + '/edit')}
                        />
                        
                        <div className="row justify-content-end">
                            <div className="col-auto">
                                <ButtonSaveForm
                                    callbackSave={update}
                                    locked={fetchingUpdateTransport}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <LazyLoading />
                )}
            </DefaultCard>

            {/* Modal de confirmación de cambio de estado */}
            {showStatusConfirmModal && pendingStatus && (
                <>
                    <div className="modal-backdrop fade show" />
                    <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar cambio de estado</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowStatusConfirmModal(false);
                                            setPendingStatus(null);
                                            setStartDateForInTransit('');
                                            setEndDateForFinalize('');
                                        }}
                                        aria-label="Cerrar"
                                    />
                                </div>
                                <div className="modal-body">
                                    <p>
                                        ¿Desea confirmar el cambio de estado a <strong>{pendingStatus.label}</strong>?
                                    </p>
                                    {pendingStatus.value === 'IN_TRANSIT' && (
                                        <div className="mb-3">
                                            <label className="form-label">Fecha de inicio</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={startDateForInTransit}
                                                onChange={(e) => setStartDateForInTransit(e.target.value)}
                                            />
                                            <small className="text-muted">Se registrará al pasar a En tránsito.</small>
                                        </div>
                                    )}
                                    {pendingStatus.value === 'FINALIZED' && (
                                        <div className="mb-3">
                                            <label className="form-label">Fecha de fin</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={endDateForFinalize}
                                                onChange={(e) => setEndDateForFinalize(e.target.value)}
                                            />
                                            <small className="text-muted">Se registrará al finalizar el transporte.</small>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowStatusConfirmModal(false);
                                            setPendingStatus(null);
                                            setStartDateForInTransit('');
                                            setEndDateForFinalize('');
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        disabled={
                                            (pendingStatus.value === 'IN_TRANSIT' && !startDateForInTransit.trim()) ||
                                            (pendingStatus.value === 'FINALIZED' && !endDateForFinalize.trim())
                                        }
                                        onClick={confirmStatusChange}
                                    >
                                        Confirmar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default TransportEdit;
